import { MutationCache, QueryCache, QueryClient, QueryClientProvider } from "@tanstack/react-query";
import { TRPCClientError, httpBatchLink } from "@trpc/client";
import { useState } from "react";
import { authHeaders, logout } from "./auth.js";
import { trpc } from "./trpc.js";

/**
 * Catch UNAUTHORIZED responses from the API (HTTP 401 — JWT expired) and
 * trigger the same logout flow a manual sign-out would: clear localStorage,
 * navigate to /login. Without this, the dashboard silently shows broken
 * panels after the 8h token expiry (caught 2026-05-24 by Axiom error
 * events `GET /trpc/<batched-procedures>` returning 1ms responses with no
 * body — the auth middleware short-circuited the batch before tRPC could
 * format per-procedure errors, so the user saw nothing).
 *
 * Guarding with the module-level `redirected` flag so we only redirect
 * once even when every procedure in a batch raises a 401 (which they all
 * do, since they share one HTTP response). The flag is page-scoped — it
 * resets on the post-login navigation.
 */
let redirected = false;
function handleApiError(err: unknown) {
  if (redirected) return;
  if (err instanceof TRPCClientError && err.data?.httpStatus === 401) {
    redirected = true;
    // Defer one tick so React Query / observers settle cleanly before
    // the page unloads — avoids "state update on unmounted component" warnings.
    setTimeout(() => logout(), 0);
  }
}

/**
 * The dashboard proxies /api/intel/* to the ECS API via a Vercel rewrite, and Vercel's proxy
 * caps responses at ~30s. Heavy synchronous endpoints (LLM copy generation, big Axiom recipe
 * queries) can exceed that — Vercel then returns a 504 with a PLAIN-TEXT body ("upstream request
 * timeout"), which tRPC blindly JSON.parses, surfacing the cryptic "Unexpected token 'u', \"upstream
 * r\"... is not valid JSON" to the user. Intercept gateway statuses (502/503/504) and throw a clear,
 * honest message instead. Scoped to gateway codes only so the 401-expiry logout flow is untouched.
 */
async function fetchThroughGateway(
  input: RequestInfo | URL,
  init?: RequestInit,
): Promise<Response> {
  const res = await fetch(input, init);
  // Expired-session 401s don't always arrive as well-formed tRPC errors — the auth
  // middleware can short-circuit with a plain body, which tRPC turns into a JSON-parse
  // Error that handleApiError never matches. Result: no logout redirect, and polling
  // UIs (e.g. the Generate-copy modal) spin on 401s forever (Jon, 2026-07-17).
  // Intercept at the fetch layer so ANY 401 triggers the one-shot logout flow.
  if (res.status === 401) {
    if (!redirected) {
      redirected = true;
      setTimeout(() => logout(), 0);
    }
    throw new Error("Your session expired — redirecting to sign-in.");
  }
  if (res.status === 502 || res.status === 503 || res.status === 504) {
    throw new Error(
      "The request timed out at the gateway (operations over ~30s can't complete synchronously). " +
        "If you triggered a long job (copy generation, a large recipe), it may still finish in the " +
        "background — give it a moment and refresh.",
    );
  }
  return res;
}

export function TrpcProvider({ children }: { children: React.ReactNode }) {
  const [queryClient] = useState(
    () =>
      new QueryClient({
        defaultOptions: { queries: { staleTime: 30_000, retry: 1 } },
        queryCache: new QueryCache({ onError: handleApiError }),
        mutationCache: new MutationCache({ onError: handleApiError }),
      }),
  );
  const [trpcClient] = useState(() =>
    trpc.createClient({
      links: [
        httpBatchLink({ url: "/api/intel/trpc", headers: authHeaders, fetch: fetchThroughGateway }),
      ],
    }),
  );
  return (
    <trpc.Provider client={trpcClient} queryClient={queryClient}>
      <QueryClientProvider client={queryClient}>{children}</QueryClientProvider>
    </trpc.Provider>
  );
}
